/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `max_met_data` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class MaxMetDataMesg : Mesg {
    public constructor() : super("MaxMetData", Profile.MesgNum.MAX_MET_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** update_time. Time maxMET and vo2 were calculated */
    public var updateTime: Instant?
        get() = (getFieldValue(UPDATE_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(UPDATE_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** vo2_max, in mL/kg/min. */
    public var vo2Max: Double?
        get() = getFieldValue(VO2_MAX_FIELD_NUM) as? Double
        set(value) = setFieldValue(VO2_MAX_FIELD_NUM, value)

    /** sport. */
    public var sport: Sport?
        get() = (getFieldValue(SPORT_FIELD_NUM) as? UByte)?.let { Sport.fromValue(it) }
        set(value) = setFieldValue(SPORT_FIELD_NUM, value?.value)

    /** sub_sport. */
    public var subSport: SubSport?
        get() = (getFieldValue(SUB_SPORT_FIELD_NUM) as? UByte)?.let { SubSport.fromValue(it) }
        set(value) = setFieldValue(SUB_SPORT_FIELD_NUM, value?.value)

    /** max_met_category. */
    public var maxMetCategory: MaxMetCategory?
        get() = (getFieldValue(MAX_MET_CATEGORY_FIELD_NUM) as? UByte)?.let { MaxMetCategory.fromValue(it) }
        set(value) = setFieldValue(MAX_MET_CATEGORY_FIELD_NUM, value?.value)

    /** calibrated_data. Indicates if calibrated data was used in the calculation */
    public var calibratedData: Boolean?
        get() = (getFieldValue(CALIBRATED_DATA_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(CALIBRATED_DATA_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** hr_source. Indicates if the estimate was obtained using a chest strap or wrist heart rate */
    public var hrSource: MaxMetHeartRateSource?
        get() = (getFieldValue(HR_SOURCE_FIELD_NUM) as? UByte)?.let { MaxMetHeartRateSource.fromValue(it) }
        set(value) = setFieldValue(HR_SOURCE_FIELD_NUM, value?.value)

    /** speed_source. Indidcates if the estimate was obtained using onboard GPS or connected GPS */
    public var speedSource: MaxMetSpeedSource?
        get() = (getFieldValue(SPEED_SOURCE_FIELD_NUM) as? UByte)?.let { MaxMetSpeedSource.fromValue(it) }
        set(value) = setFieldValue(SPEED_SOURCE_FIELD_NUM, value?.value)

    public companion object {
        public const val UPDATE_TIME_FIELD_NUM: UByte = 0u
        public const val VO2_MAX_FIELD_NUM: UByte = 2u
        public const val SPORT_FIELD_NUM: UByte = 5u
        public const val SUB_SPORT_FIELD_NUM: UByte = 6u
        public const val MAX_MET_CATEGORY_FIELD_NUM: UByte = 8u
        public const val CALIBRATED_DATA_FIELD_NUM: UByte = 9u
        public const val HR_SOURCE_FIELD_NUM: UByte = 12u
        public const val SPEED_SOURCE_FIELD_NUM: UByte = 13u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[0] = Field("UpdateTime", 0u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[2] = Field("Vo2Max", 2u, BaseType.UINT16, 10.0, 0.0, "mL/kg/min", false)
            fields[5] = Field("Sport", 5u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[6] = Field("SubSport", 6u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[8] = Field("MaxMetCategory", 8u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[9] = Field("CalibratedData", 9u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[12] = Field("HrSource", 12u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[13] = Field("SpeedSource", 13u, BaseType.ENUM, 1.0, 0.0, "", false)
            return fields
        }
    }
}
